import { contextStore } from "../auth/context-store.js";
import { ensureActiveClientContext } from "../auth/tenant-context.js";
import * as authGateway from "../auth/auth-gateway.js";
import { router } from "../router/router.js";
import { platform } from "../../app/browser-platform.js";
import { RoleCode } from "../../permissions/permissions.js";
import { DEFAULT_LOGIN } from "../../domain/studio/visual-design.js";
import type { LoginAppearance } from "../../application/services/studio-design-service.js";
import { loadAndApplyAppearance } from "../studio/appearance.js";
import { IS_BACKEND_MODE } from "../../app/browser-platform.js";
import { resolveAppVersion } from "../../shared/branding/app-version.js";

/** Dica NÃO autoritativa (só a cor da tela de carregamento); o desenho vem sempre do servidor. */
const SHELL_HINT_KEY = "inspect_login_shell_hint";
const APPEARANCE_TIMEOUT_MS = 4000;

declare const __APP_VERSION__: string;
declare const __APP_BUILD_TIME__: string;
declare const __APP_BACKEND_ENV__: string;

export default class LoginPage extends HTMLElement {
  private showPassword = false;
  private errorMessage = "";
  private isLoading = false;
  /** Só para o estado visual do botão (item 26) — a validação do envio continua sendo a do formulário. */
  private username = "";
  private password = "";

  /** STUDIO INSPECT IA: a tela de login PUBLICADA (ou a do rascunho, no VISUALIZAR LOGIN). */
  private appearance: LoginAppearance | null = null;
  private previewMode = false;
  /**
   * Enquanto o desenho PUBLICADO não chega, nada de login de fábrica na tela:
   * desenhar o padrão e trocar depois era o "piscar" — e, se a leitura
   * atrasasse, parecia que o Studio não tinha publicado nada.
   */
  private appearanceLoading = false;

  private canSubmit(): boolean {
    return this.username.trim().length > 0 && this.password.length > 0;
  }

  /** VISUALIZAR LOGIN (Studio): mostra o desenho informado, sem autenticar nada. */
  set previewAppearance(appearance: LoginAppearance) {
    this.previewMode = true;
    this.appearance = appearance;
    this.setAttribute("data-preview", "");
    if (this.isConnected) this.render();
  }

  connectedCallback() {
    // O Studio cria <app-login-page data-preview> e só depois entrega o desenho.
    if (this.hasAttribute("data-preview")) this.previewMode = true;
    if (!this.previewMode) this.appearanceLoading = true;
    this.render();
    if (!this.previewMode) void this.loadAppearance();
  }

  /** O desenho PUBLICADO: no servidor pelo endpoint público; no modo local, pela plataforma do navegador. */
  private async fetchPublished(): Promise<LoginAppearance> {
    if (IS_BACKEND_MODE) {
      const res = await fetch("/api/public/login-appearance", { cache: "no-store", credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as LoginAppearance;
    }
    const service = (platform as any).studioDesignService;
    if (!service) throw new Error("Studio indisponível");
    return service.getPublicLoginAppearance();
  }

  private async loadAppearance() {
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("tempo esgotado")), APPEARANCE_TIMEOUT_MS));
    try {
      this.appearance = await Promise.race([this.fetchPublished(), timeout]);
      try { localStorage.setItem(SHELL_HINT_KEY, this.appearance.login.backgroundColor); } catch { /* dica opcional */ }
    } catch (e) {
      // Fallback CONTROLADO: sem desenho publicado legível, vale o login de
      // fábrica — só nesta tela, nada é gravado nem publicado.
      console.warn("[LOGIN] Desenho publicado indisponível; usando o login padrão INSPECT IA.", e);
      this.appearance = null;
    } finally {
      this.appearanceLoading = false;
      if (this.isConnected) this.render();
    }
  }

  // ─────────────────────────────── textos e estilos vindos do Studio

  private esc(value: string): string {
    return value.replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
  }

  /** Texto escapado; `*trecho*` do Studio vira destaque na cor de destaque do login. */
  private rich(value: string): string {
    return this.esc(value).replace(/\*([^*]+)\*/g, '<b class="lg-hl">$1</b>');
  }

  /** Texto escapado, com quebra de linha do Studio (\n) virando <br>. */
  private multiline(value: string): string {
    return value.split("\n").map(line => this.rich(line)).join("<br>");
  }

  private get L() {
    return this.appearance?.login ?? DEFAULT_LOGIN;
  }

  /** "INSPECT IA" mantém a marcação de sempre (duas cores); outro título aprovado sai como texto. */
  private brandTitle(first: string, second: string): string {
    const title = this.L.title.trim();
    return title.toUpperCase() === "INSPECT IA" ? `<span class="${first}">INSPECT</span>${second === "ia" ? "" : " "}<span class="${second}">IA</span>` : this.esc(title);
  }

  private footerLine(): string {
    return this.L.footerText.split(" | ").map(part => this.rich(part)).join(' <span class="foot-sep">|</span> ');
  }

  /**
   * VERSÃO-05 (Padronização Global de Nome + Versionamento Dinâmico): a marca
   * ("INSPECT IA", vinda do Studio/§53) e a versão nunca se fundem no mesmo
   * texto. A versão é lida da fonte central (`resolveAppVersion`), nunca do
   * `localStorage`. Ambiente não-produção aparece rotulado ao lado, para não
   * ser confundido com a versão (RC não pode "vazar" como se fosse igual).
   */
  private versionLine(): string {
    const version = typeof __APP_VERSION__ !== "undefined" && __APP_VERSION__ ? __APP_VERSION__ : null;
    const buildNumber = typeof __APP_BUILD_TIME__ !== "undefined" && __APP_BUILD_TIME__ ? __APP_BUILD_TIME__ : null;
    const environment = typeof __APP_BACKEND_ENV__ !== "undefined" && __APP_BACKEND_ENV__ ? __APP_BACKEND_ENV__ : null;
    const appVersion = resolveAppVersion({ version, buildNumber, environment });
    const envLabel = appVersion.environment === "production" ? "" : ` <span class="foot-sep">|</span> ${this.esc(appVersion.environment.toUpperCase())}`;
    const statusLabel = appVersion.releaseStatus === "RC" && appVersion.environment !== "production" ? ` <span class="foot-sep">|</span> RC` : "";
    return `${this.esc(appVersion.displayVersion)}${envLabel}${statusLabel}`;
  }

  private studioOverrides(): string {
    const L = this.L;
    const d = DEFAULT_LOGIN;
    const css: string[] = [
      "app-login-page .lg-hl { color: var(--gold); font-weight: 700; }",
      // Fonte publicada explícita: igual no /login (sem sessão) e no preview (dentro do app).
      ...(this.appearance?.fontStack && /^[\w\s",.-]+$/.test(this.appearance.fontStack) ? [`app-login-page, app-login-page * { font-family: ${this.appearance.fontStack}; }`] : []),
      "app-login-page .brand-logo-img { display: block; max-width: min(460px, 82%); max-height: 112px; object-fit: contain; margin: 0 0 8px; }",
      "app-login-page .card-brand-logo { display: block; max-width: 80%; max-height: 66px; object-fit: contain; margin: 6px auto 0; }"
    ];
    if (L.variant === "CLARO") css.push(this.lightVariantCss());
    if (L.backgroundColor !== d.backgroundColor) css.push(`app-login-page, app-login-page .login-wrapper { background-color: ${L.backgroundColor} !important; }`);
    if (L.accentColor !== d.accentColor) css.push(`app-login-page .login-wrapper { --gold: ${L.accentColor}; --gold-soft: ${L.accentColor}; }`);
    const bg = this.appearance?.backgroundImageUrl;
    if (bg && /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(bg)) css.push(`app-login-page .left-bg { background-image: url("${bg}"); }`);
    if (L.overlayOpacity !== d.overlayOpacity) css.push(`app-login-page .left-bg-overlay { opacity: ${Math.max(0, Math.min(1, L.overlayOpacity))}; }`);
    if (L.buttonColor) css.push(`app-login-page .login-submit { background: ${L.buttonColor}; }`);
    if (L.cardPosition === "ESQUERDA") {
      css.push(`@media (min-width: 769px) { app-login-page .left-panel { order: 2; } app-login-page .right-panel { order: 1; } }`);
      css.push(`@media (min-width: 1101px) { app-login-page .login-wrapper { grid-template-columns: 44.4% 55.6%; } }`);
      css.push(`@media (min-width: 769px) and (max-width: 1100px) { app-login-page .login-wrapper { grid-template-columns: 58% 42%; } }`);
    }
    if (L.cardPosition === "CENTRO") {
      // O mesmo arranjo que o login já usa no celular: imagem de fundo inteira e o card ao centro.
      css.push(`@media (min-width: 769px) {
        app-login-page .login-wrapper { grid-template-columns: 1fr; }
        app-login-page .left-panel { position: absolute; inset: 0; padding: 0; z-index: 0; }
        app-login-page .left-panel > *:not(.left-bg):not(.left-bg-overlay) { display: none; }
        app-login-page .right-panel { background: transparent; align-items: center; justify-content: center; z-index: 1; }
        app-login-page .right-panel::before { display: none; }
        app-login-page .login-card { width: min(560px, 92%); }
      }`);
    }
    if (this.previewMode) {
      // VISUALIZAR LOGIN: contido na moldura do Studio, sem cobrir a aplicação.
      css.push(`app-login-page[data-preview] { position: relative !important; inset: auto !important; width: 100% !important; height: 100% !important; z-index: auto !important; }
        app-login-page[data-preview] .login-wrapper { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; z-index: auto !important; }
        app-login-page[data-preview] #btn-toggle-fullscreen { display: none; }`);
    }
    return css.join("\n");
  }

  /**
   * Variante CLARA (Studio): fundo cinza claro com a planta industrial em
   * tons de cinza, texto grafite, card claro e destaque na cor escolhida. É
   * só aparência — o formulário, a validação e a autenticação são os mesmos.
   */
  private lightVariantCss(): string {
    const bg = this.L.backgroundColor;
    return `
      app-login-page, app-login-page .login-wrapper { background-color: ${bg} !important; color: #23282D; }
      app-login-page .login-wrapper { --ink: #1F2429; --muted: #4B545C; --faint: #6B737B; }
      app-login-page .left-bg { filter: grayscale(1) brightness(1.75) contrast(0.82); }
      app-login-page .left-bg-overlay { background:
        linear-gradient(180deg, rgba(236,238,240,0.55) 0%, rgba(236,238,240,0.18) 38%, rgba(236,238,240,0.80) 100%),
        linear-gradient(90deg, rgba(236,238,240,0.94) 0%, rgba(236,238,240,0.40) 55%, rgba(236,238,240,0.12) 100%); }
      app-login-page .frame-line { background: linear-gradient(180deg, transparent, rgba(0,0,0,0.12) 12%, rgba(0,0,0,0.12) 88%, transparent); }
      app-login-page .decor-arc { border-color: rgba(0,0,0,0.08); border-bottom-color: transparent; border-left-color: transparent; }
      app-login-page .decor-text { color: #3A4148; }
      app-login-page .brand-title .inspect, app-login-page .ct-inspect, app-login-page .lg-i { color: #23282D; }
      app-login-page .brand-title .ia, app-login-page .ct-ia, app-login-page .lg-x { color: var(--gold); }
      app-login-page .brand-subtitle { color: #2F353B; }
      app-login-page .brand-desc, app-login-page .benefit span { color: #3F474E; }
      app-login-page .benefit svg { color: #2F353B; }
      app-login-page .benefit-sep { background: rgba(0,0,0,0.16); }
      app-login-page .right-panel { background: linear-gradient(160deg, #EEF0F2 0%, #E2E5E8 60%, #D9DDE1 100%); }
      app-login-page .right-panel::before { background-image: radial-gradient(rgba(0,0,0,0.06) 1px, transparent 1px); }
      app-login-page .top-right-nav, app-login-page .top-right-nav .nav-item { color: #3A4148; }
      app-login-page .top-right-nav .sep { color: rgba(0,0,0,0.28); }
      app-login-page .btn-fullscreen-top { background: rgba(255,255,255,0.65); border-color: rgba(0,0,0,0.14); color: #3A4148; }
      app-login-page .login-card { background: linear-gradient(168deg, rgba(248,249,250,0.97) 0%, rgba(238,240,242,0.97) 100%); border-color: rgba(0,0,0,0.08); box-shadow: 0 18px 50px rgba(0,0,0,0.12); }
      app-login-page .login-logo { background: linear-gradient(160deg, #F6F7F8 0%, #E4E7EA 100%); border-color: rgba(0,0,0,0.08); }
      app-login-page .login-card-subtitle, app-login-page .footer-motto, app-login-page .footer-security, app-login-page .footer-version { color: #3F474E; }
      app-login-page .login-label { color: #2F353B; }
      app-login-page .input-shell { background: #FFFFFF; border-color: rgba(0,0,0,0.14); }
      app-login-page .input-shell:focus-within { background: #FFFFFF; border-color: var(--gold); }
      app-login-page .login-input { color: #1F2429; }
      app-login-page .login-input::placeholder { color: #8A9299; }
      app-login-page .login-input:-webkit-autofill { -webkit-text-fill-color: #1F2429; -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset; }
      app-login-page .input-icon, app-login-page .btn-toggle-pwd { color: #4B545C; }
      app-login-page .login-submit { color: #FFFFFF; }
      app-login-page .login-error { background: rgba(220,38,38,0.08); }
      app-login-page .login-error-title { color: #7F1D1D; }
      app-login-page .login-error-message { color: #991B1B; }
      @media (max-width: 768px) {
        app-login-page .left-bg-overlay { background: linear-gradient(180deg, rgba(236,238,240,0.88), rgba(236,238,240,0.94)); }
        app-login-page .right-panel { background: transparent; }
      }`;
  }

  private render() {
    if (this.appearanceLoading) {
      let hint = "#0A0D11";
      try { hint = localStorage.getItem(SHELL_HINT_KEY) || hint; } catch { /* sem dica */ }
      const safe = /^#[0-9a-fA-F]{6}$/.test(hint) ? hint : "#0A0D11";
      this.innerHTML = `<div class="login-boot" style="position:fixed;inset:0;background:${safe};" aria-busy="true" aria-label="Carregando a tela de acesso"></div>`;
      return;
    }
    this.innerHTML = `
      <style>
        /* ============================================================
           TELA DE LOGIN — reprodução fiel da referência aprovada.
           Proporções tiradas da própria imagem (base 1672 x 941):
           painel esquerdo 55,6% · card 620px centrado no painel direito ·
           marca ~80px · benefícios acima do rodapé · rodapé a 94% da altura.
           Somente interface: nenhuma regra de autenticação passa por aqui.
           ============================================================ */
        app-login-page, :host {
          display: block !important;
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          height: 100dvh !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
          background-color: #0A0D11 !important;
          font-family: var(--font-sans, "Inter", "Segoe UI", sans-serif);
          color: #F2F4F6;
          box-sizing: border-box;
          z-index: 99999 !important;
        }
        app-login-page *, app-login-page *::before, app-login-page *::after { box-sizing: border-box; }

        .login-wrapper {
          --gold: #C9A227;
          --gold-soft: #E0C97A;
          --ink: #F2F4F6;
          --muted: #A8B0B8;
          --faint: #7D858D;
          display: grid;
          grid-template-columns: 55.6% 44.4%;
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100dvh;
          overflow: hidden;
          background: #0A0D11;
          z-index: 99999;
        }

        /* ---------------------------------------------- PAINEL ESQUERDO */
        .left-panel {
          position: relative;
          display: flex;
          flex-direction: column;
          padding: clamp(28px, 3.4vw, 58px) clamp(28px, 3.6vw, 62px);
          overflow: hidden;
        }
        .left-bg {
          position: absolute;
          inset: 0;
          background-image: url('/industrial_bg.jpg');
          background-size: cover;
          background-position: center 42%;
          z-index: 0;
        }
        /* Contraste sem apagar o detalhe da planta industrial. */
        .left-bg-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(180deg, rgba(8,11,15,0.58) 0%, rgba(8,11,15,0.28) 34%, rgba(8,11,15,0.86) 100%),
            linear-gradient(90deg, rgba(8,11,15,0.60) 0%, rgba(8,11,15,0.16) 46%, rgba(8,11,15,0.56) 100%);
          z-index: 1;
        }
        /* Moldura técnica: hairline vertical e arco discretos. */
        .frame-line {
          position: absolute;
          top: 0;
          bottom: 0;
          left: clamp(16px, 2.6vw, 46px);
          width: 1px;
          background: linear-gradient(180deg, transparent, rgba(255,255,255,0.13) 12%, rgba(255,255,255,0.13) 88%, transparent);
          z-index: 2;
        }
        .decor-arc {
          position: absolute;
          top: -22%;
          left: 26%;
          width: 62%;
          aspect-ratio: 1;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.07);
          border-bottom-color: transparent;
          border-left-color: transparent;
          z-index: 2;
          pointer-events: none;
        }

        /*
         * Conteúdo em camada acima do fundo. Os ENFEITES (linha, arco, pilha
         * PRECISÃO/SEGURANÇA) ficam FORA desta regra: ela tem especificidade
         * maior que a deles e trocava o position:absolute por relative — o
         * arco (~60% da largura, quadrado) entrava no fluxo e empurrava a
         * marca e o texto para fora da tela, cortando a parte inferior.
         */
        .left-panel > *:not(.left-bg):not(.left-bg-overlay):not(.frame-line):not(.decor-arc):not(.decor-stack) { position: relative; z-index: 3; }
        .decor-stack { z-index: 3; }

        .decor-text {
          font-size: clamp(0.58rem, 0.66vw, 0.7rem);
          letter-spacing: 0.22em;
          line-height: 1.95;
          text-transform: uppercase;
          color: var(--muted);
          font-weight: 400;
        }

        .left-top-block { display: flex; align-items: flex-start; gap: 14px; }
        .gold-bar { width: 2px; height: 38px; background: var(--gold); flex-shrink: 0; margin-top: 2px; }

        .decor-stack {
          position: absolute;
          top: clamp(150px, 20vh, 205px);
          left: 48%;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .gold-dash { width: 22px; height: 2px; background: var(--gold); }

        /* Marca: bloco inferior-esquerdo, como na referência. */
        .brand-section {
          margin-top: auto;
          margin-bottom: clamp(56px, 8.5vh, 92px);
          display: flex;
          flex-direction: column;
          max-width: 760px;
        }
        .brand-title {
          display: flex;
          gap: clamp(10px, 1vw, 18px);
          margin: 0;
          font-size: clamp(2.6rem, 4.9vw, 5rem);
          font-weight: 700;
          line-height: 0.98;
          letter-spacing: -0.015em;
        }
        .brand-title .inspect { color: #FFFFFF; }
        .brand-title .ia { color: var(--gold-soft); }

        .brand-subtitle {
          margin-top: clamp(10px, 1.2vh, 16px);
          font-size: clamp(0.95rem, 1.28vw, 1.32rem);
          font-weight: 400;
          color: #DFE4E9;
        }
        .brand-rule {
          width: 68px;
          height: 4px;
          border-radius: 2px;
          background: var(--gold);
          margin: clamp(16px, 2vh, 26px) 0 clamp(18px, 2.2vh, 28px);
        }
        .brand-desc {
          font-size: clamp(0.88rem, 1.14vw, 1.19rem);
          line-height: 1.55;
          color: #C5CCD3;
          max-width: 560px;
        }

        .benefits {
          display: flex;
          align-items: center;
          gap: clamp(18px, 2.2vw, 34px);
          margin-top: clamp(30px, 4.4vh, 56px);
        }
        .benefit { display: flex; align-items: center; gap: 13px; }
        .benefit svg { width: clamp(20px, 1.6vw, 26px); height: clamp(20px, 1.6vw, 26px); color: #DDE3E9; flex-shrink: 0; }
        .benefit span {
          font-size: clamp(0.56rem, 0.63vw, 0.68rem);
          letter-spacing: 0.17em;
          line-height: 1.75;
          text-transform: uppercase;
          color: #C5CCD3;
        }
        .benefit-sep { width: 1px; height: 46px; background: rgba(255,255,255,0.15); }

        .bottom-text-row { margin-top: auto; }
        .foot-sep { color: var(--gold); margin: 0 10px; }

        /* ----------------------------------------------- PAINEL DIREITO */
        .right-panel {
          position: relative;
          display: flex;
          flex-direction: column;
          padding: clamp(24px, 2.6vw, 44px) clamp(20px, 2.4vw, 40px);
          background: radial-gradient(120% 90% at 78% 0%, #131A22 0%, #0C1116 52%, #090D11 100%);
        }
        /* Textura técnica quase invisível (nada de fundo chapado). */
        .right-panel::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px);
          background-size: 22px 22px;
          -webkit-mask-image: radial-gradient(75% 55% at 88% 6%, #000 0%, transparent 72%);
          mask-image: radial-gradient(75% 55% at 88% 6%, #000 0%, transparent 72%);
          pointer-events: none;
        }
        .right-panel > * { position: relative; z-index: 1; }

        .top-right-nav {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: clamp(10px, 1.1vw, 18px);
          font-size: clamp(0.56rem, 0.63vw, 0.68rem);
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--muted);
        }
        .top-right-nav .sep { color: rgba(201,162,39,0.75); }

        .btn-fullscreen-top {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-left: clamp(12px, 1.4vw, 26px);
          padding: 8px 12px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 7px;
          color: var(--muted);
          font-family: inherit;
          font-size: 0.6rem;
          letter-spacing: 0.16em;
          line-height: 1.5;
          text-transform: uppercase;
          text-align: left;
          cursor: pointer;
          transition: border-color .15s ease, color .15s ease;
        }
        .btn-fullscreen-top:hover { border-color: rgba(255,255,255,0.28); color: var(--ink); }
        .btn-fullscreen-top svg { width: 15px; height: 15px; flex-shrink: 0; }
        .btn-fullscreen-top span { display: block; max-width: 42px; }

        .login-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 0;
        }

        .login-card {
          width: 84%;
          max-width: 620px;
          padding: clamp(30px, 3.6vh, 48px) clamp(24px, 3.2vw, 52px);
          background: linear-gradient(168deg, rgba(23,32,42,0.92) 0%, rgba(14,20,27,0.94) 58%, rgba(11,16,21,0.96) 100%);
          border: 1px solid rgba(120,145,170,0.26);
          border-top-color: rgba(201,162,39,0.34);
          border-radius: 18px;
          box-shadow: 0 26px 70px rgba(0,0,0,0.46);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .login-logo {
          width: clamp(62px, 4.6vw, 76px);
          height: clamp(62px, 4.6vw, 76px);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1px;
          background: linear-gradient(160deg, #1B242E 0%, #10161C 100%);
          border: 1px solid rgba(140,165,190,0.24);
          border-radius: 14px;
          font-size: clamp(1.4rem, 1.7vw, 1.75rem);
          font-weight: 700;
          letter-spacing: 0.01em;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 22px rgba(0,0,0,0.35);
        }
        .lg-i { color: #FFFFFF; }
        .lg-x { color: var(--gold-soft); }

        .login-card-title {
          margin: clamp(16px, 2vh, 24px) 0 0;
          font-size: clamp(1.5rem, 2.3vw, 2.35rem);
          font-weight: 700;
          letter-spacing: -0.01em;
          text-align: center;
        }
        .ct-inspect { color: #FFFFFF; }
        .ct-ia { color: var(--gold-soft); }

        .login-card-subtitle {
          margin: clamp(6px, 0.9vh, 12px) 0 clamp(22px, 3.2vh, 40px);
          font-size: clamp(0.78rem, 0.92vw, 0.95rem);
          color: var(--muted);
          text-align: center;
        }

        .login-error {
          display: none;
          width: 100%;
          margin-bottom: 20px;
          padding: 13px 16px;
          gap: 12px;
          align-items: flex-start;
          background: rgba(150,28,28,0.16);
          border: 1px solid rgba(220,80,80,0.32);
          border-radius: 9px;
        }
        .login-error.visible { display: flex; }
        .login-error svg { width: 19px; height: 19px; flex-shrink: 0; margin-top: 1px; color: #F0A2A2; }
        .login-error-content { display: flex; flex-direction: column; gap: 3px; }
        .login-error-title { font-size: 0.8rem; font-weight: 600; color: #F4DADA; }
        .login-error-message { font-size: 0.78rem; color: #E2C9C9; line-height: 1.45; }

        .login-form { width: 100%; display: flex; flex-direction: column; gap: clamp(14px, 2.2vh, 22px); }
        .login-field { display: flex; flex-direction: column; gap: 10px; }
        .login-label {
          font-size: clamp(0.62rem, 0.72vw, 0.76rem);
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #D3D9DF;
        }

        .input-shell {
          position: relative;
          display: flex;
          align-items: center;
          height: clamp(50px, 6.2vh, 58px);
          background: rgba(9,13,17,0.72);
          border: 1px solid rgba(120,145,170,0.26);
          border-radius: 10px;
          transition: border-color .15s ease, background .15s ease;
        }
        .input-shell:focus-within { border-color: rgba(201,162,39,0.55); background: rgba(9,13,17,0.9); }
        .input-icon { display: flex; align-items: center; justify-content: center; width: 50px; color: var(--faint); flex-shrink: 0; }
        .input-icon svg { width: 18px; height: 18px; }

        .login-input {
          flex: 1;
          min-width: 0;
          height: 100%;
          padding: 0 14px 0 0;
          background: transparent;
          border: none;
          outline: none;
          color: var(--ink);
          font-family: inherit;
          font-size: clamp(0.86rem, 1vw, 1rem);
        }
        .login-input::placeholder { color: #6B747D; }
        .login-input:-webkit-autofill { -webkit-text-fill-color: var(--ink); -webkit-box-shadow: 0 0 0 1000px #0C1116 inset; }

        .btn-toggle-pwd {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 52px;
          height: 100%;
          background: none;
          border: none;
          color: var(--faint);
          cursor: pointer;
        }
        .btn-toggle-pwd:hover { color: var(--ink); }
        .btn-toggle-pwd svg { width: 19px; height: 19px; }

        .login-submit {
          margin-top: clamp(10px, 1.6vh, 18px);
          width: 100%;
          height: clamp(54px, 6.8vh, 64px);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: linear-gradient(135deg, #E8CE7E 0%, #D5B155 42%, #C9A227 100%);
          color: #14181C;
          border: none;
          border-radius: 10px;
          font-family: inherit;
          font-size: clamp(0.95rem, 1.08vw, 1.1rem);
          font-weight: 700;
          letter-spacing: 0.01em;
          cursor: pointer;
          transition: filter .18s ease, box-shadow .18s ease;
        }
        .login-submit svg { width: 19px; height: 19px; }
        .login-submit:hover:not(:disabled) { filter: brightness(1.06); box-shadow: 0 8px 22px rgba(201,162,39,0.22); }
        .login-submit:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
        .login-spinner { animation: login-spin 0.9s linear infinite; }
        @keyframes login-spin { to { transform: rotate(360deg); } }

        .btn-toggle-pwd:focus-visible,
        .btn-fullscreen-top:focus-visible,
        .login-submit:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }

        .card-footer { width: 100%; margin-top: clamp(20px, 3vh, 34px); }
        .footer-divider { position: relative; height: 1px; background: rgba(255,255,255,0.10); }
        .footer-dash {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 28px;
          height: 3px;
          border-radius: 2px;
          background: var(--gold);
        }
        .footer-motto {
          margin-top: clamp(14px, 2vh, 22px);
          text-align: center;
          font-size: clamp(0.54rem, 0.62vw, 0.66rem);
          letter-spacing: 0.2em;
          line-height: 2;
          text-transform: uppercase;
          color: var(--faint);
        }
        .footer-security { display: none; }
        .footer-version {
          margin-top: clamp(8px, 1.2vh, 12px);
          text-align: center;
          font-size: clamp(0.5rem, 0.58vw, 0.6rem);
          letter-spacing: 0.16em;
          line-height: 1.6;
          text-transform: uppercase;
          color: var(--faint);
          opacity: 0.72;
        }

        /* ------------------------------------------------ RESPONSIVO */
        @media (max-width: 1400px) {
          .login-card { width: 90%; }
          .benefits { gap: 18px; }
        }

        /* Tablet: o institucional encolhe, o card cresce. */
        @media (max-width: 1100px) {
          .login-wrapper { grid-template-columns: 42% 58%; }
          .decor-stack, .decor-arc { display: none; }
          .benefits { gap: 14px; }
          .benefit span { font-size: 0.54rem; }
          .benefit-sep { height: 36px; }
          .login-card { width: 94%; }
          .top-right-nav .nav-item, .top-right-nav .sep { display: none; }
        }

        /* Mobile: o painel institucional vira fundo; o Login é a prioridade. */
        @media (max-width: 768px) {
          .login-wrapper {
            grid-template-columns: 1fr;
            padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
          }
          .left-panel {
            position: absolute;
            inset: 0;
            padding: 0;
            z-index: 0;
          }
          .left-panel > *:not(.left-bg):not(.left-bg-overlay) { display: none; }
          .left-bg-overlay { background: linear-gradient(180deg, rgba(8,11,15,0.90), rgba(8,11,15,0.96)); }
          .right-panel {
            background: transparent;
            padding: 0;
            align-items: center;
            justify-content: center;
            z-index: 1;
          }
          .right-panel::before { display: none; }
          .top-right-nav { display: none; }
          .login-container { width: 100%; padding: 16px 0; overflow-y: auto; }
          .login-card {
            width: calc(100% - 32px);
            margin: 16px;
            max-width: 460px;
            padding: 30px 22px;
            border-radius: 16px;
          }
          .footer-motto { display: none; }
          .footer-security {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            margin-top: 16px;
            font-size: 0.56rem;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: var(--faint);
          }
          .footer-security svg { width: 14px; height: 14px; }
        }

        @media (max-height: 700px) and (min-width: 769px) {
          .login-card { padding: 26px 40px; }
          .login-card-subtitle { margin-bottom: 20px; }
        }

        /* Tela baixa (notebook 1366×768 com a barra do navegador, zoom 125%…): a
           pilha decorativa PRECISÃO/SEGURANÇA/DESEMPENHO cairia sobre o título. */
        @media (max-height: 820px) and (min-width: 769px) {
          .decor-stack { display: none; }
        }
      </style>
      <style>${this.studioOverrides()}</style>

      <div class="login-wrapper">
        <div class="left-panel">
          <div class="left-bg"></div>
          <div class="left-bg-overlay"></div>
          <div class="frame-line"></div>
          <div class="decor-arc"></div>

          <div class="left-top-block">
            <span class="gold-bar"></span>
            <div class="decor-text">${this.multiline(this.L.slogan)}</div>
          </div>

          <div class="decor-stack">
            <span class="gold-dash"></span>
            <div class="decor-text">PRECISÃO<br>SEGURANÇA<br>DESEMPENHO</div>
          </div>

          <div class="brand-section">
            ${this.appearance?.brandLogoUrl ? `<img class="brand-logo-img" src="${this.esc(this.appearance.brandLogoUrl)}" alt="">` : ""}
            <h1 class="brand-title">
              ${this.brandTitle("inspect", "ia")}
            </h1>
            <div class="brand-subtitle">${this.esc(this.L.subtitle)}</div>
            <div class="brand-rule"></div>
            <div class="brand-desc">
              ${this.multiline(this.L.institutionalText)}
            </div>

            <div class="benefits">
              <div class="benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span>INSPEÇÃO<br>CONFIÁVEL</span>
              </div>
              <span class="benefit-sep"></span>
              <div class="benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20v-6M9 20V7M14 20v-9M19 20V4"/></svg>
                <span>DECISÕES<br>BASEADAS EM DADOS</span>
              </div>
              <span class="benefit-sep"></span>
              <div class="benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.36.4.66.73.86"/></svg>
                <span>ATIVOS<br>MAIS SEGUROS</span>
              </div>
            </div>
          </div>

          <div class="bottom-text-row">
            <div class="decor-text">${this.footerLine()}</div>
          </div>
        </div>

        <div class="right-panel">
          <div class="top-right-nav">
            <span class="nav-item">PESSOAS</span> <span class="sep">|</span>
            <span class="nav-item">PROCESSOS</span> <span class="sep">|</span>
            <span class="nav-item">INTEGRIDADE</span> <span class="sep">|</span>
            <span class="nav-item">RESULTADOS</span>
            <button type="button" id="btn-toggle-fullscreen" class="btn-fullscreen-top" title="Ativar ou desativar tela cheia" aria-label="Alternar tela cheia">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
              <span id="fullscreen-label">TELA CHEIA</span>
            </button>
          </div>

          <div class="login-container">
            <div class="login-card">
              ${this.appearance?.loginLogoUrl
                ? `<div class="login-logo login-logo-image" aria-hidden="true"><img src="${this.esc(this.appearance.loginLogoUrl)}" alt="" style="max-width:100%;max-height:100%;object-fit:contain;"></div>`
                : '<div class="login-logo" aria-hidden="true"><span class="lg-i">I</span><span class="lg-x">X</span></div>'}
              ${this.appearance?.brandLogoUrl ? `<img class="card-brand-logo" src="${this.esc(this.appearance.brandLogoUrl)}" alt="">` : ""}
              <h2 class="login-card-title">${this.brandTitle("ct-inspect", "ct-ia")}</h2>
              <p class="login-card-subtitle">${this.rich(this.L.cardSubtitle)}</p>

              <div id="login-error" class="login-error ${this.errorMessage ? 'visible' : ''}" role="alert" aria-live="polite">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <div class="login-error-content">
                  <span class="login-error-title">Não foi possível realizar o acesso.</span>
                  <span class="login-error-message">${this.errorMessage}</span>
                </div>
              </div>

              <form id="form-login" class="login-form" novalidate>
                <div class="login-field">
                  <label class="login-label" for="input-username">Login / E-mail</label>
                  <div class="input-shell">
                    <span class="input-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </span>
                    <input type="text" id="input-username" class="login-input" value="${this.username.replace(/"/g, "&quot;")}" placeholder="Ex: admin ou email@empresa.com" autocomplete="username" spellcheck="false" required>
                  </div>
                </div>

                <div class="login-field">
                  <label class="login-label" for="input-password">Senha de Acesso</label>
                  <div class="input-shell">
                    <span class="input-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </span>
                    <input type="${this.showPassword ? 'text' : 'password'}" id="input-password" class="login-input" value="${this.password.replace(/"/g, "&quot;")}" placeholder="Digite sua senha" autocomplete="current-password" required>
                    <button type="button" id="btn-toggle-login-pwd" class="btn-toggle-pwd" aria-label="${this.showPassword ? 'Ocultar senha' : 'Exibir senha'}" aria-pressed="${this.showPassword}">
                      ${this.showPassword
                        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="m1 1 22 22"/></svg>'
                        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>'}
                    </button>
                  </div>
                </div>

                <button type="submit" id="btn-submit-login" class="login-submit" ${this.isLoading || !this.canSubmit() ? 'disabled' : ''}>
                  ${this.isLoading
                    ? '<svg class="login-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity=".28" stroke-width="2.5"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg><span>Entrando...</span>'
                    : `<span>${this.esc(this.L.buttonLabel)}</span>` + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h13M14 8l4 4-4 4"/></svg>'}
                </button>
              </form>

              <div class="card-footer">
                <div class="footer-divider"><span class="footer-dash"></span></div>
                <div class="footer-motto">
                  ${this.multiline(this.L.cardMotto)}
                </div>
                <div class="footer-security">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>
                  </svg>
                  AMBIENTE SEGURO &bull; ACESSO RESTRITO
                </div>
                <div class="footer-version">${this.versionLine()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const updateFullscreenBtn = () => {
      const label = this.querySelector("#fullscreen-label");
      if (label) {
        label.textContent = document.fullscreenElement ? "REDUZIR" : "TELA CHEIA";
      }
    };
    updateFullscreenBtn();

    this.querySelector("#btn-toggle-fullscreen")?.addEventListener("click", async () => {
      try {
        if (!document.fullscreenElement) {
          if (document.documentElement.requestFullscreen) {
            await document.documentElement.requestFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          }
        }
      } catch (err) {
        console.warn("Fullscreen toggle:", err);
      }
      updateFullscreenBtn();
    });

    document.addEventListener("fullscreenchange", updateFullscreenBtn);

    const usernameField = this.querySelector("#input-username") as HTMLInputElement | null;
    const passwordField = this.querySelector("#input-password") as HTMLInputElement | null;
    const submitButton = this.querySelector("#btn-submit-login") as HTMLButtonElement | null;
    const syncSubmitState = () => {
      this.username = usernameField?.value ?? "";
      this.password = passwordField?.value ?? "";
      if (submitButton && !this.isLoading) submitButton.disabled = !this.canSubmit();
    };
    usernameField?.addEventListener("input", syncSubmitState);
    passwordField?.addEventListener("input", syncSubmitState);
    // Preenchimento automático do navegador não dispara "input": sincroniza já no bind.
    syncSubmitState();

    this.querySelector("#btn-toggle-login-pwd")?.addEventListener("click", () => {
      this.showPassword = !this.showPassword;
      this.render();
    });

    this.querySelector("#form-login")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      // VISUALIZAR LOGIN do Studio: é só desenho — nada é enviado.
      if (this.previewMode) return;

      try {
        if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.warn("Não foi possível ativar tela cheia:", err);
      }

      if (this.isLoading) return;

      const usernameInput = (this.querySelector("#input-username") as HTMLInputElement).value.trim();
      const passwordInput = (this.querySelector("#input-password") as HTMLInputElement).value;

      if (!usernameInput || !passwordInput) {
        this.errorMessage = "Verifique seu login e senha.";
        this.render();
        return;
      }

      this.isLoading = true;
      this.render();

      try {
        // A porta de autenticação decide o transporte: servidor em
        // homologação, repositório local no teste local (mesma fonte do
        // cadastro de Usuários). A tela não conhece nenhum dos dois.
        const result = await authGateway.login(usernameInput, passwordInput);

        if (!result.ok) {
          this.errorMessage = result.message;
          this.isLoading = false;
          this.render();
          return;
        }

        const session = result.session;
        // Perfil e Professional NÃO são gravados no navegador: a cada carga
        // são relidos da origem por `restoreSession()`. Ver context-store.ts.
        contextStore.login(session.name, session.userId, session.role, {
          professionalId: session.professionalId,
          ...(session.customPermissions ? { customPermissions: session.customPermissions } : {})
        });

        // Resolve active tenant/client context before navigation
        await ensureActiveClientContext();

        // Identidade publicada no Studio aplicada antes da primeira tela interna.
        await loadAndApplyAppearance();
        router.navigate("/");

      } catch (err: any) {
        console.error("Erro na autenticação:", err);
        this.errorMessage = "Erro ao autenticar. Tente novamente.";
        this.isLoading = false;
        this.render();
      }
    });
  }
}

if (!customElements.get("app-login-page")) {
  customElements.define("app-login-page", LoginPage);
}

